/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import { configureStore } from '@reduxjs/toolkit';
import { CorrectionState, correction } from './correction';
import { webSocket, WebSocketState } from './webSocket';
import { AuthState, auth } from './auth';
import { FeedbackState, feedback } from './feedback';
import { ChatState, chat } from './chat';
import { ConfigurationState, configuration } from './configuration';
import { CommonState, common } from './common';
import { WritingStyleState, writingStyle } from './writing-style';
import { AppState, app } from './app';
import { EditorState, editor } from './editor';
import { ComposeState, compose } from './compose';
import { ReferenceState, reference } from './reference';
import { GlossaryState, glossary } from './glossary';
import { TranslationState, translation } from './translation';
import { ConnectorState, connector } from './connector';
import { AiAssistantState, aiAssistant } from './ai-assistant';
import { ArticleState, article } from './article';
import { ReferenceStoreState, referenceStore } from './reference-store';
import {
  WritingInstructionState,
  writingInstruction,
} from './writing-instruction';
import { ConnectorFilterState, connectorFilter } from './connector-filter';
import {
  ComposeInstructionState,
  composeInstruction,
} from './compose-instruction';
import { WritingRuleState, writingRule } from './wirting-rule';
import { InspectState, inspect } from './inspect';
import { ThinkingState, thinking } from './thinking';
export type RootState = {
  correction: CorrectionState;
  webSocket: WebSocketState;
  auth: AuthState;
  feedback: FeedbackState;
  chat: ChatState;
  configuration: ConfigurationState;
  common: CommonState;
  writingStyle: WritingStyleState;
  app: AppState;
  compose: ComposeState;
  editor: EditorState;
  reference: ReferenceState;
  glossary: GlossaryState;
  translation: TranslationState;
  connector: ConnectorState;
  aiAssistant: AiAssistantState;
  article: ArticleState;
  referenceStore: ReferenceStoreState;
  writingInstruction: WritingInstructionState;
  connectorFilter: ConnectorFilterState;
  composeInstruction: ComposeInstructionState;
  writingRule: WritingRuleState;
  inspect: InspectState;
  thinking: ThinkingState;
};

export const initStore = () =>
  configureStore({
    reducer: {
      correction: correction.reducer,
      webSocket: webSocket.reducer,
      auth: auth.reducer,
      feedback: feedback.reducer,
      chat: chat.reducer,
      configuration: configuration.reducer,
      common: common.reducer,
      writingStyle: writingStyle.reducer,
      app: app.reducer,
      compose: compose.reducer,
      editor: editor.reducer,
      reference: reference.reducer,
      glossary: glossary.reducer,
      translation: translation.reducer,
      connector: connector.reducer,
      aiAssistant: aiAssistant.reducer,
      article: article.reducer,
      referenceStore: referenceStore.reducer,
      writingInstruction: writingInstruction.reducer,
      connectorFilter: connectorFilter.reducer,
      composeInstruction: composeInstruction.reducer,
      writingRule: writingRule.reducer,
      inspect: inspect.reducer,
      thinking: thinking.reducer,
    },
  });

let store: ReturnType<typeof initStore>;

export const getStore = () => {
  if (!store) {
    store = initStore();
  }
  (window as any).store = store;
  return store;
};

(window as any).getStore = getStore;
